import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  mapScenarioFromApi,
  toCreateChangeRequest,
  toCreateScenarioRequest,
  toUpdateChangeRequest,
} from "../api/mappers/scenarioMapper";
import type {
  ScenarioListResponseApi,
  UpdateScenarioRequestApi,
} from "../api/models/scenario";
import * as scenarioApi from "../api/services/scenarioService";
import { ACTIVITIES } from "../data/activity-data";
import {
  COMPREHENSIVE_SCENARIOS,
  SCENARIO_COLLECTIONS,
} from "../data/scenario-data";
import { useBaseForecast } from "../hooks/useBaseForecast";
import { calculateScenarioMetrics } from "../lib/scenarioEngine";
import type { Activity } from "../types/activity";
import type {
  ComprehensiveScenario,
  ScenarioApproval,
  ScenarioAuditEntry,
  ScenarioAuditEventType,
  ScenarioChange,
  ScenarioCollection,
  ScenarioComment,
  ScenarioLabel,
  ScenarioPriority,
  ScenarioShare,
  ScenarioStatus,
  ScenarioVersion,
} from "../types/scenarios";
import { useApp } from "./AppContext";
import { ScenarioContext, ScenarioContextType } from "./ScenarioContextDef";

// Compute the effective permission for a demo scenario based on ownership and shares
function getEffectivePermission(
  scenario: ComprehensiveScenario,
  currentUser: string,
): ComprehensiveScenario["currentUserPermission"] {
  if (scenario.createdBy === currentUser) return "owner";
  const share = scenario.shares.find((s) => s.userId === currentUser);
  if (share)
    return share.permission as ComprehensiveScenario["currentUserPermission"];
  return null;
}

export const ScenarioProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentUser, mode } = useApp();
  const isDemo = mode === "demo";
  const queryClient = useQueryClient();

  // Helper: invalidate all scenario list queries (REST + GraphQL views)
  // so that ScenarioListGallery and ParkedDashboard reflect mutations immediately.
  const invalidateScenarioLists = () => {
    queryClient.invalidateQueries({ queryKey: ["scenarios"] });
    queryClient.invalidateQueries({ queryKey: ["scenarioViews"] });
    queryClient.invalidateQueries({ queryKey: ["scenarioViewsCount"] });
    queryClient.invalidateQueries({ queryKey: ["updatingScenarios"] });
    // Every scenario mutation writes a ScenarioAuditLog row, so the Audit Log
    // tab is stale as soon as one lands. Invalidated by prefix because the
    // callers here do not all know the numeric scenario id.
    queryClient.invalidateQueries({ queryKey: ["scenarioAuditLog"] });
  };

  // Helper: the Sharing tab reads vw_ScenarioAccess over GraphQL, which is a
  // separate cache from the scenario lists — share mutations must invalidate it
  // too or the tab keeps rendering the pre-mutation access list.
  const invalidateScenarioAccess = (scenarioId: string) => {
    queryClient.invalidateQueries({
      queryKey: ["scenarioAccess", parseInt(scenarioId, 10)],
    });
    queryClient.invalidateQueries({ queryKey: ["scenario-share-users"] });
  };

  // ── Base forecast — from shared context ────────────────────────────────────
  // This is the foundation every scenario is measured against.
  // In demo mode: generated from demo-data.ts
  // In live mode: fetched from API via BaseForecastProvider
  const { baseForecast } = useBaseForecast();

  // ── Scenario metrics calculation ───────────────────────────────────────────
  // Helper: recalculate metrics for a single scenario using the engine.
  // Called on init and whenever a scenario's changes are updated.
  const recalculateMetrics = (
    scenario: ComprehensiveScenario,
  ): ComprehensiveScenario => {
    // Guard: if base forecast hasn't loaded yet, return scenario unchanged
    // (metrics will be recalculated once baseForecast is populated)
    if (baseForecast.length === 0) {
      return scenario;
    }

    // Base scenario has no changes — metrics come directly from the base forecast
    if (scenario.id === "base" || scenario.changes.length === 0) {
      const lastPoint = baseForecast[baseForecast.length - 1];
      const firstPoint = baseForecast[0];
      let minBalance = firstPoint.balance;
      let minDate = firstPoint.date;
      let totalOut = 0;
      let totalIn = 0;
      for (const point of baseForecast) {
        if (point.balance < minBalance) {
          minBalance = point.balance;
          minDate = point.date;
        }
        totalIn += point.incoming;
        totalOut += point.outgoing;
      }
      const avgDailyBurn = (totalOut - totalIn) / baseForecast.length;
      const runway =
        avgDailyBurn > 0 ? firstPoint.balance / avgDailyBurn / 30.44 : Infinity;

      return {
        ...scenario,
        metrics: {
          minCash: Math.round(minBalance),
          minCashDate: minDate,
          runway: isFinite(runway)
            ? runway > 0 && runway < 0.1
              ? 0.1
              : Math.round(runway * 10) / 10
            : Infinity,
          revenueChange: 0,
          revenueChangeAmount: 0,
          costChange: 0,
          costChangeAmount: 0,
          netImpact: 0,
          endingCash: Math.round(lastPoint.balance),
        },
      };
    }

    // All other scenarios: run the engine
    const metrics = calculateScenarioMetrics(baseForecast, scenario.changes);
    return { ...scenario, metrics };
  };

  // -- Live mode: Fetch scenarios from API -----------------------------------
  const { data: apiScenariosData } = useQuery({
    queryKey: ["scenarios"],
    queryFn: scenarioApi.getScenarios,
    enabled: !isDemo,
    staleTime: 30_000,
    refetchInterval: isDemo ? false : 30_000,
    refetchOnWindowFocus: !isDemo,
  });

  // Fetch changes for each scenario in parallel
  const scenarioIds = apiScenariosData?.scenarios?.map((s) => s.id) ?? [];
  const changesResult = useQueries({
    queries: scenarioIds.map((id) => ({
      queryKey: ["scenarioChanges", id],
      queryFn: () => scenarioApi.getScenarioChanges(id),
      enabled: !isDemo && scenarioIds.length > 0,
      staleTime: 30_000,
    })),
    combine: (results) => ({
      data: results.map((r) => r.data),
      isPending: results.some((r) => r.isPending),
    }),
  });

  // Combine scenarios + changes using the mapper, then recalculate metrics
  // Comments and versions are lazy-loaded in the detail view only
  const liveScenarios = useMemo(() => {
    if (isDemo || !apiScenariosData?.scenarios) return [];
    return apiScenariosData.scenarios.map((apiScenario, index) => {
      const changesData = changesResult.data[index]?.changes ?? [];
      const mapped = mapScenarioFromApi(apiScenario, changesData);
      return recalculateMetrics(mapped);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiScenariosData, changesResult.data, isDemo, baseForecast]);

  // -- Demo mode state (only used when isDemo) --------------------------------
  // Always load fresh demo data - NO localStorage persistence
  // Immediately recalculate metrics for all scenarios using the engine
  const [demoScenarios, setDemoScenarios] = useState<ComprehensiveScenario[]>(
    () => COMPREHENSIVE_SCENARIOS.map(recalculateMetrics),
  );
  const [collections, setCollections] =
    useState<ScenarioCollection[]>(SCENARIO_COLLECTIONS);
  const [activities, setActivities] = useState<Activity[]>(ACTIVITIES);

  // Demo-mode scenario refresh timers — keyed by scenario id so a manual
  // refresh can transition freshness 'updating' -> 'live' after ~3s.
  const demoRefreshTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  useEffect(() => {
    const timers = demoRefreshTimers.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  // -- Pending approvals (live mode) ----------------------------------------
  const { data: pendingApprovalsData } = useQuery({
    queryKey: ["pendingApprovals"],
    queryFn: scenarioApi.getPendingApprovals,
    enabled: !isDemo,
    staleTime: 30_000,
    refetchInterval: isDemo ? false : 30_000,
    refetchOnWindowFocus: !isDemo,
  });

  // Demo-mode stage-assignee mapping (mirrors DEMO_WORKFLOW_STAGES in ScenarioWorkflowActions)
  const DEMO_WORKFLOW_STAGES: Record<number, string> = { 1: "4", 2: "1" };

  const pendingApprovalScenarios: ComprehensiveScenario[] = useMemo(() => {
    if (isDemo) {
      return demoScenarios.filter((s) => {
        if (s.status !== "pending-approval" && s.status !== "under-review")
          return false;
        if (s.createdBy === currentUser.id && !s.allowSelfApproval)
          return false;
        const stageOrder = s.currentStageOrder ?? 1;
        const stageAssignee = DEMO_WORKFLOW_STAGES[stageOrder];
        if (stageAssignee !== currentUser.id) return false;
        if (s.approvalHistory?.some((a) => a.userId === currentUser.id)) {
          return false;
        }
        return true;
      });
    }
    if (!pendingApprovalsData) return [];
    return pendingApprovalsData
      .map((apiScenario) => mapScenarioFromApi(apiScenario))
      .filter((s) => {
        if (s.status !== "pending-approval" && s.status !== "under-review")
          return false;
        if (s.createdBy === currentUser.id && !s.allowSelfApproval)
          return false;
        return true;
      });
  }, [isDemo, demoScenarios, pendingApprovalsData, currentUser.id]);

  const pendingApprovalCount = pendingApprovalScenarios.length;

  // The scenarios exposed to consumers - demo or live
  // In demo mode, compute currentUserPermission client-side for each scenario
  const scenariosWithPermission = useMemo(() => {
    if (!isDemo) return demoScenarios; // unused in live mode
    return demoScenarios.map((s) => ({
      ...s,
      currentUserPermission: getEffectivePermission(s, currentUser.id),
    }));
  }, [isDemo, demoScenarios, currentUser.id]);

  const scenarios = isDemo ? scenariosWithPermission : liveScenarios;

  const setCurrentUser = () => {}; // No-op — use AppContext setCurrentUser instead

  // Helper to get current user name
  const getCurrentUserName = () => {
    return currentUser.name;
  };

  // ── Audit log helper ───────────────────────────────────────────────────────
  // Appends a ScenarioAuditEntry to a scenario's auditLog in demo mode.
  // In live mode the backend owns the audit trail — this is a no-op.
  const appendAuditEntry = (
    scenarioId: string,
    eventType: ScenarioAuditEventType,
    fields?: Partial<
      Omit<
        ScenarioAuditEntry,
        | "id"
        | "scenarioId"
        | "timestamp"
        | "actorId"
        | "actorName"
        | "eventType"
      >
    >,
  ) => {
    if (!isDemo) return;
    const entry: ScenarioAuditEntry = {
      id: `audit-${scenarioId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      scenarioId,
      timestamp: new Date(),
      actorId: currentUser.id,
      actorName: getCurrentUserName(),
      eventType,
      ...fields,
    };
    setDemoScenarios((prev) =>
      prev.map((s) =>
        s.id === scenarioId
          ? { ...s, auditLog: [...(s.auditLog ?? []), entry] }
          : s,
      ),
    );
  };

  // Scenario CRUD
  const createScenario = async (
    scenario: Omit<ComprehensiveScenario, "id" | "createdAt" | "modifiedAt"> & {
      _dependencies?: { targetId: string; type: string }[];
    },
  ): Promise<string> => {
    // Extract _dependencies from the scenario object so they travel as a single param
    const { _dependencies, ...scenarioData } = scenario;
    if (isDemo) {
      const newScenario: ComprehensiveScenario = {
        ...scenario,
        id: `scenario-${Date.now()}`,
        createdAt: new Date(),
        modifiedAt: new Date(),
        auditLog: [
          {
            id: `audit-${Date.now()}-created`,
            scenarioId: `scenario-${Date.now()}`,
            timestamp: new Date(),
            actorId: currentUser.id,
            actorName: getCurrentUserName(),
            eventType: "created",
            toStatus: "draft",
          },
        ],
      };
      setDemoScenarios((prev) => [...prev, newScenario]);

      addActivity({
        type: "scenario-created",
        scenarioId: newScenario.id,
        scenarioName: newScenario.name,
        userId: currentUser.id,
        userName: getCurrentUserName(),
        message: `${getCurrentUserName()} created "${newScenario.name}"`,
      });

      return newScenario.id;
    }

    // Live mode: call API, create dependencies, then invalidate query cache
    const request = toCreateScenarioRequest(scenarioData);
    try {
      const response = await scenarioApi.createScenario(request);
      const newId = response.id;

      // Create dependencies if provided (embedded in the scenario object as _dependencies)
      if (_dependencies && _dependencies.length > 0) {
        await Promise.all(
          _dependencies.map((dep) =>
            scenarioApi
              .createScenarioDependency(newId, {
                targetScenarioId: parseInt(dep.targetId, 10),
                dependencyType: dep.type,
              })
              .catch((error) => {
                console.error("Failed to create dependency:", error);
              }),
          ),
        );
      }

      invalidateScenarioLists();
      return String(newId);
    } catch (error) {
      console.error("Failed to create scenario:", error);
      return "pending";
    }
  };

  const updateScenario = async (
    id: string,
    updates: Partial<ComprehensiveScenario>,
  ): Promise<void> => {
    if (isDemo) {
      setDemoScenarios((prev) => {
        const updated = prev.map((scenario) => {
          if (scenario.id !== id) return scenario;
          const merged = {
            ...scenario,
            ...updates,
            modifiedAt: new Date(),
            modifiedBy: getCurrentUserName(),
          };
          return updates.changes !== undefined
            ? recalculateMetrics(merged)
            : merged;
        });
        return updated;
      });

      // Only log a generic edit activity when the user is changing scenario
      // content (name, description, changes, etc.) — not for internal status
      // transitions which are logged by their own specific workflow functions.
      const isStatusOnlyUpdate = Object.keys(updates).every((k) =>
        [
          "status",
          "currentApprover",
          "assignedApprover",
          "parkedAt",
          "parkedBy",
          "parkedReason",
          "reviewDate",
          "reviewedAt",
          "reviewedBy",
        ].includes(k),
      );

      if (!isStatusOnlyUpdate) {
        const scenario = scenarios.find((s) => s.id === id);
        if (scenario) {
          addActivity({
            type: "scenario-edited",
            scenarioId: id,
            scenarioName: scenario.name,
            userId: currentUser.id,
            userName: getCurrentUserName(),
            message: `${getCurrentUserName()} updated "${scenario.name}"`,
          });
          appendAuditEntry(id, "edited");
        }
      }
      return;
    }

    // Live mode: collect all API promises so callers can await completion
    const apiPromises: Promise<void>[] = [];
    const numericId = parseInt(id, 10);

    // Handle scalar field updates via the Scenario Update API
    const apiUpdates: UpdateScenarioRequestApi = {};
    if (updates.name !== undefined) apiUpdates.name = updates.name;
    // The update API treats a null field as "leave unchanged", so a cleared
    // description is sent as an empty string rather than null.
    if (updates.description !== undefined)
      apiUpdates.description = updates.description ?? "";
    if (updates.status !== undefined) apiUpdates.status = updates.status;
    if (updates.priority !== undefined) apiUpdates.priority = updates.priority;
    if (updates.label !== undefined) apiUpdates.label = updates.label || null;
    if (updates.category !== undefined)
      apiUpdates.category = updates.category || null;
    if (updates.startingPoint !== undefined)
      apiUpdates.startingPoint = updates.startingPoint || null;
    if (updates.parkedReason !== undefined)
      apiUpdates.parkedReason = updates.parkedReason || null;
    if (updates.reviewDate !== undefined) {
      apiUpdates.reviewDate = updates.reviewDate
        ? updates.reviewDate.toISOString()
        : null;
    }

    if (Object.keys(apiUpdates).length > 0) {
      apiPromises.push(
        scenarioApi
          .updateScenario(numericId, apiUpdates)
          .then(() => {
            invalidateScenarioLists();
            if (updates.status !== undefined) {
              queryClient.invalidateQueries({ queryKey: ["pendingApprovals"] });
            }
          })
          .catch((error) => {
            console.error("Failed to update scenario:", error);
            // Propagate so awaiting callers can surface the failure instead of
            // reporting a false success.
            throw error;
          }),
      );
    }

    // Handle dependencies array updates by diffing against current state
    if (
      updates.dependencies !== undefined &&
      Array.isArray(updates.dependencies)
    ) {
      const currentScenario = scenarios.find((s) => s.id === id);
      const currentDeps =
        currentScenario?.dependencies &&
        Array.isArray(currentScenario.dependencies)
          ? currentScenario.dependencies
          : [];
      const newDeps = updates.dependencies;
      const currentDepIds = new Set(currentDeps.map((d) => d.id));
      const newDepIds = new Set(newDeps.map((d) => d.id));

      // Added dependencies: present in new array but not in current
      const addedDeps = newDeps.filter((d) => !currentDepIds.has(d.id));
      for (const dep of addedDeps) {
        scenarioApi
          .createScenarioDependency(numericId, {
            targetScenarioId: parseInt(dep.targetScenarioId, 10),
            dependencyType: dep.type,
            impact: dep.impact ?? undefined,
          })
          .then(() => {
            invalidateScenarioLists();
          })
          .catch((error) => {
            console.error("Failed to add dependency:", error);
          });
      }

      // Removed dependencies: present in current but not in new
      const removedDeps = currentDeps.filter((d) => !newDepIds.has(d.id));
      for (const dep of removedDeps) {
        const numericDepId = parseInt(dep.id, 10);
        if (!isNaN(numericDepId)) {
          scenarioApi
            .deleteScenarioDependency(numericId, numericDepId)
            .then(() => {
              invalidateScenarioLists();
            })
            .catch((error) => {
              console.error("Failed to remove dependency:", error);
            });
        }
      }
    }

    // Handle changes array updates by diffing against current state
    if (updates.changes !== undefined) {
      const currentScenario = scenarios.find((s) => s.id === id);
      const currentChanges = currentScenario?.changes ?? [];
      const newChanges = updates.changes;
      const currentIds = new Set(currentChanges.map((c) => c.id));
      const newIds = new Set(newChanges.map((c) => c.id));

      // Added changes: present in new array but not in current
      const added = newChanges.filter((c) => !currentIds.has(c.id));
      for (const change of added) {
        const request = toCreateChangeRequest(change);
        apiPromises.push(
          scenarioApi
            .createScenarioChange(numericId, request)
            .then(() => {
              invalidateScenarioLists();
              queryClient.invalidateQueries({
                queryKey: ["scenarioChanges", numericId],
              });
            })
            .catch((error) => {
              console.error("Failed to add change:", error);
            }),
        );
      }

      // Removed changes: present in current but not in new
      const removed = currentChanges.filter((c) => !newIds.has(c.id));
      for (const change of removed) {
        const numericChangeId = parseInt(change.id, 10);
        if (!isNaN(numericChangeId)) {
          apiPromises.push(
            scenarioApi
              .deleteScenarioChange(numericId, numericChangeId)
              .then(() => {
                invalidateScenarioLists();
                queryClient.invalidateQueries({
                  queryKey: ["scenarioChanges", numericId],
                });
              })
              .catch((error) => {
                console.error("Failed to remove change:", error);
              }),
          );
        }
      }

      // Updated changes: present in both but content may have changed.
      // Compare by serializing to detect actual modifications, then
      // delete + recreate so every field (including type) is refreshed.
      const currentById = new Map(currentChanges.map((c) => [c.id, c]));
      const updated = newChanges.filter((c) => {
        if (!currentIds.has(c.id)) return false; // already handled as "added"
        const prev = currentById.get(c.id);
        return prev && JSON.stringify(prev) !== JSON.stringify(c);
      });
      for (const change of updated) {
        const numericChangeId = parseInt(change.id, 10);
        if (!isNaN(numericChangeId)) {
          // Delete the old version then create the replacement
          apiPromises.push(
            scenarioApi
              .deleteScenarioChange(numericId, numericChangeId)
              .then(() => {
                const request = toCreateChangeRequest(change);
                return scenarioApi.createScenarioChange(numericId, request);
              })
              .then(() => {
                invalidateScenarioLists();
                queryClient.invalidateQueries({
                  queryKey: ["scenarioChanges", numericId],
                });
              })
              .catch((error) => {
                console.error("Failed to update change:", error);
              }),
          );
        }
      }
    }

    // Wait for all API calls to complete before resolving
    await Promise.all(apiPromises);
  };

  const deleteScenario = (id: string) => {
    if (isDemo) {
      const scenario = scenarios.find((s) => s.id === id);
      setDemoScenarios((prev) => prev.filter((scenario) => scenario.id !== id));
      if (scenario) {
        addActivity({
          type: "scenario-deleted",
          scenarioId: id,
          scenarioName: scenario.name,
          userId: currentUser.id,
          userName: getCurrentUserName(),
          message: `${getCurrentUserName()} deleted "${scenario.name}"`,
        });
      }
      return;
    }

    // Live mode
    const numericId = parseInt(id, 10);
    scenarioApi
      .deleteScenario(numericId)
      .then(() => {
        invalidateScenarioLists();
      })
      .catch((error) => {
        console.error("Failed to delete scenario:", error);
      });
  };

  // Clone a scenario into a new draft named "<name> (Copy)".
  // Live mode: the backend duplicates all adjustments and re-runs the engine.
  // Demo mode: replicate the existing spread-and-create behaviour (carries changes).
  const cloneScenario = async (id: string): Promise<void> => {
    const scenario = scenarios.find((s) => s.id === id);
    if (!scenario) return;

    if (isDemo) {
      const clonedScenario = {
        ...scenario,
        name: `${scenario.name} (Copy)`,
        status: "draft" as const,
        createdByName: scenario.createdByName,
        isFavorite: false,
        commentCount: 0,
        comments: [],
        shares: [],
        collectionIds: [],
        tags: [...scenario.tags],
        changes: [...scenario.changes],
      };
      await createScenario(clonedScenario);
      return;
    }

    // Live mode: POST to the clone endpoint, then refresh the lists.
    const numericId = parseInt(id, 10);
    try {
      await scenarioApi.cloneScenario(numericId);
      invalidateScenarioLists();
    } catch (error) {
      console.error("Failed to clone scenario:", error);
    }
  };

  // Favorites
  const toggleFavorite = (id: string) => {
    if (isDemo) {
      setDemoScenarios((prev) =>
        prev.map((scenario) =>
          scenario.id === id
            ? { ...scenario, isFavorite: !scenario.isFavorite }
            : scenario,
        ),
      );
      return;
    }

    // Live mode: toggle via dedicated endpoint
    const numericId = parseInt(id, 10);
    scenarioApi
      .toggleScenarioFavorite(numericId)
      .then(() => {
        invalidateScenarioLists();
      })
      .catch((error) => {
        console.error("Failed to toggle favorite:", error);
      });
  };

  // Priority & Status
  const setPriority = (id: string, priority: ScenarioPriority) => {
    if (isDemo) {
      updateScenario(id, { priority });
      return;
    }
    const numericId = parseInt(id, 10);
    scenarioApi
      .updateScenario(numericId, { priority })
      .then(() => {
        invalidateScenarioLists();
      })
      .catch((error) => {
        console.error("Failed to set priority:", error);
      });
  };

  const setStatus = (id: string, status: ScenarioStatus) => {
    if (isDemo) {
      const scenario = scenarios.find((s) => s.id === id);
      updateScenario(id, { status });
      if (scenario) {
        if (status === "archived") {
          addActivity({
            type: "scenario-archived",
            scenarioId: id,
            scenarioName: scenario.name,
            userId: currentUser.id,
            userName: getCurrentUserName(),
            message: `${getCurrentUserName()} archived "${scenario.name}"`,
          });
          appendAuditEntry(id, "archived", {
            fromStatus: scenario.status,
            toStatus: "archived",
          });
        } else if (status === "pending-approval") {
          addActivity({
            type: "approval-forwarded",
            scenarioId: id,
            scenarioName: scenario.name,
            userId: currentUser.id,
            userName: getCurrentUserName(),
            message: `${getCurrentUserName()} sent "${scenario.name}" directly for approval`,
          });
          appendAuditEntry(id, "submitted-for-approval", {
            fromStatus: scenario.status,
            toStatus: "pending-approval",
          });
        } else {
          appendAuditEntry(id, "status-changed", {
            fromStatus: scenario.status,
            toStatus: status,
          });
        }
      }
      return;
    }
    const numericId = parseInt(id, 10);
    scenarioApi
      .updateScenario(numericId, { status })
      .then(() => {
        invalidateScenarioLists();
        queryClient.invalidateQueries({ queryKey: ["pendingApprovals"] });
      })
      .catch((error) => {
        console.error("Failed to set status:", error);
      });
  };

  const setLabel = (id: string, label: ScenarioLabel) => {
    if (isDemo) {
      updateScenario(id, { label });
      return;
    }
    const numericId = parseInt(id, 10);
    scenarioApi
      .updateScenario(numericId, { label: label || null })
      .then(() => {
        invalidateScenarioLists();
      })
      .catch((error) => {
        console.error("Failed to set label:", error);
      });
  };

  // Changes
  const addChange = (
    scenarioId: string,
    change: Omit<ScenarioChange, "id" | "createdAt" | "createdBy">,
  ) => {
    if (isDemo) {
      const scenario = scenarios.find((s) => s.id === scenarioId);
      if (!scenario) return;

      const newChange: ScenarioChange = {
        ...change,
        id: `change-${Date.now()}`,
        createdAt: new Date(),
        createdBy: getCurrentUserName(),
      };

      updateScenario(scenarioId, {
        changes: [...scenario.changes, newChange],
      });
      return;
    }

    // Live mode
    const numericScenarioId = parseInt(scenarioId, 10);
    const request = toCreateChangeRequest(change);
    scenarioApi
      .createScenarioChange(numericScenarioId, request)
      .then(() => {
        invalidateScenarioLists();
        queryClient.invalidateQueries({
          queryKey: ["scenarioChanges", numericScenarioId],
        });
      })
      .catch((error) => {
        console.error("Failed to add change:", error);
      });
  };

  const updateChange = (
    scenarioId: string,
    changeId: string,
    updates: Partial<ScenarioChange>,
  ) => {
    if (isDemo) {
      const scenario = scenarios.find((s) => s.id === scenarioId);
      if (!scenario) return;

      updateScenario(scenarioId, {
        changes: scenario.changes.map((change) =>
          change.id === changeId ? { ...change, ...updates } : change,
        ),
      });
      return;
    }

    // Live mode
    const numericScenarioId = parseInt(scenarioId, 10);
    const numericChangeId = parseInt(changeId, 10);
    const request = toUpdateChangeRequest(updates);
    scenarioApi
      .updateScenarioChange(numericScenarioId, numericChangeId, request)
      .then(() => {
        invalidateScenarioLists();
        queryClient.invalidateQueries({
          queryKey: ["scenarioChanges", numericScenarioId],
        });
      })
      .catch((error) => {
        console.error("Failed to update change:", error);
      });
  };

  const removeChange = (scenarioId: string, changeId: string) => {
    if (isDemo) {
      const scenario = scenarios.find((s) => s.id === scenarioId);
      if (!scenario) return;

      updateScenario(scenarioId, {
        changes: scenario.changes.filter((change) => change.id !== changeId),
      });
      return;
    }

    // Live mode
    const numericScenarioId = parseInt(scenarioId, 10);
    const numericChangeId = parseInt(changeId, 10);
    scenarioApi
      .deleteScenarioChange(numericScenarioId, numericChangeId)
      .then(() => {
        invalidateScenarioLists();
        queryClient.invalidateQueries({
          queryKey: ["scenarioChanges", numericScenarioId],
        });
      })
      .catch((error) => {
        console.error("Failed to remove change:", error);
      });
  };

  // Comments
  const addComment = (
    scenarioId: string,
    content: string,
    parentId?: string,
  ) => {
    if (isDemo) {
      const scenario = scenarios.find((s) => s.id === scenarioId);
      if (!scenario) return;

      const newComment: ScenarioComment = {
        id: `comment-${Date.now()}`,
        userId: currentUser.id,
        authorName: getCurrentUserName(),
        userRole: currentUser.forecasterRole,
        text: content,
        createdAt: new Date(),
        parentId,
        replies: [],
      };

      let updatedComments = [...(scenario.comments || [])];

      if (parentId) {
        // Add as reply
        updatedComments = updatedComments.map((comment) => {
          if (comment.id === parentId) {
            return {
              ...comment,
              replies: [...(comment.replies || []), newComment],
            };
          }
          return comment;
        });
      } else {
        // Add as top-level comment
        updatedComments.push(newComment);
      }

      updateScenario(scenarioId, {
        comments: updatedComments,
        commentCount: scenario.commentCount + 1,
      });

      addActivity({
        type: parentId ? "comment-reply" : "comment-added",
        scenarioId,
        scenarioName: scenario.name,
        userId: currentUser.id,
        userName: getCurrentUserName(),
        message: `${getCurrentUserName()} commented on "${scenario.name}"`,
      });
      return;
    }

    // Live mode
    const numericScenarioId = parseInt(scenarioId, 10);
    scenarioApi
      .createScenarioComment(numericScenarioId, { text: content })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["scenarioComments", numericScenarioId],
        });
        invalidateScenarioLists();
      })
      .catch((error) => {
        console.error("Failed to add comment:", error);
      });
  };

  const replyToComment = (
    scenarioId: string,
    commentId: string,
    content: string,
  ) => {
    addComment(scenarioId, content, commentId);
  };

  const reactToComment = (
    scenarioId: string,
    commentId: string,
    reaction: string,
  ) => {
    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario) return;

    const updatedComments = scenario.comments?.map((comment) => {
      if (comment.id === commentId) {
        const reactions = comment.reactions || { like: 0, love: 0 };

        // Increment the reaction count
        return {
          ...comment,
          reactions: {
            ...reactions,
            [reaction as "like" | "love"]:
              (reactions[reaction as "like" | "love"] || 0) + 1,
          },
        };
      }
      return comment;
    });

    updateScenario(scenarioId, {
      comments: updatedComments,
    });
  };

  // Sharing
  const shareScenario = async (
    scenarioId: string,
    share: Omit<ScenarioShare, "sharedAt" | "sharedBy">,
  ): Promise<void> => {
    if (isDemo) {
      const scenario = scenarios.find((s) => s.id === scenarioId);
      if (!scenario) return;

      const newShare: ScenarioShare = {
        ...share,
        sharedAt: new Date(),
        sharedBy: getCurrentUserName(),
      };

      updateScenario(scenarioId, {
        shares: [...scenario.shares, newShare],
      });

      addActivity({
        type: "scenario-shared",
        scenarioId,
        scenarioName: scenario.name,
        userId: currentUser.id,
        userName: getCurrentUserName(),
        message: `${getCurrentUserName()} shared "${scenario.name}" with ${share.userName}`,
      });
      appendAuditEntry(scenarioId, "shared", {
        detail: `Shared with ${share.userName} (${share.permission})`,
      });
      return;
    }

    // Live mode: call shares API
    const numericScenarioId = parseInt(scenarioId, 10);
    try {
      await scenarioApi.shareScenario(numericScenarioId, {
        userId: share.userId,
        permission: share.permission,
      });
      invalidateScenarioLists();
      invalidateScenarioAccess(scenarioId);
    } catch (error) {
      console.error("Failed to share scenario:", error);
      throw error;
    }
  };

  const updateShare = (
    scenarioId: string,
    userId: string,
    permission: ScenarioShare["permission"],
  ) => {
    if (isDemo) {
      const scenario = scenarios.find((s) => s.id === scenarioId);
      if (!scenario) return;

      updateScenario(scenarioId, {
        shares: scenario.shares.map((share) =>
          share.userId === userId ? { ...share, permission } : share,
        ),
      });
      return;
    }

    // Live mode
    const numericScenarioId = parseInt(scenarioId, 10);
    scenarioApi
      .updateScenarioShare(numericScenarioId, userId, { permission })
      .then(() => {
        invalidateScenarioLists();
        invalidateScenarioAccess(scenarioId);
      })
      .catch((error) => {
        console.error("Failed to update share:", error);
      });
  };

  const removeShare = (scenarioId: string, userId: string) => {
    if (isDemo) {
      const scenario = scenarios.find((s) => s.id === scenarioId);
      if (!scenario) return;

      const removedShare = scenario.shares.find(
        (share) => share.userId === userId,
      );
      updateScenario(scenarioId, {
        shares: scenario.shares.filter((share) => share.userId !== userId),
      });
      if (removedShare) {
        addActivity({
          type: "scenario-unshared",
          scenarioId,
          scenarioName: scenario.name,
          userId: currentUser.id,
          userName: getCurrentUserName(),
          message: `${getCurrentUserName()} removed ${removedShare.userName}'s access to "${scenario.name}"`,
        });
        appendAuditEntry(scenarioId, "unshared", {
          detail: `Removed ${removedShare.userName}'s access`,
        });
      }
      return;
    }

    // Live mode
    const numericScenarioId = parseInt(scenarioId, 10);
    scenarioApi
      .deleteScenarioShare(numericScenarioId, userId)
      .then(() => {
        invalidateScenarioLists();
        invalidateScenarioAccess(scenarioId);
      })
      .catch((error) => {
        console.error("Failed to remove share:", error);
      });
  };

  // Approval
  const requestApproval = async (
    scenarioId: string,
    approvers: string[],
    options?: { skipReview?: boolean; coveringNote?: string },
  ): Promise<string | undefined> => {
    if (!isDemo) {
      // Live mode: call API
      const numericId = parseInt(scenarioId, 10);
      try {
        const result = await scenarioApi.submitForApproval(numericId, {
          skipReview: options?.skipReview,
          coveringNote: options?.coveringNote,
        });
        invalidateScenarioLists();
        queryClient.invalidateQueries({ queryKey: ["pendingApprovals"] });
        return result.status;
      } catch (error) {
        console.error("Failed to submit for approval:", error);
        return undefined;
      }
    }

    // Demo mode: submit into Stage 1 of the formal approval workflow
    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario) return undefined;

    // Stage 1 approver is always approvers[0] (Linda Kowalski in the demo workflow)
    updateScenario(scenarioId, {
      status: "pending-approval",
      currentStageOrder: 1,
      currentStageName: "Finance Review",
      currentApprover: approvers[0],
    });

    addActivity({
      type: "approval-requested",
      scenarioId,
      scenarioName: scenario.name,
      userId: currentUser.id,
      userName: getCurrentUserName(),
      message: `${getCurrentUserName()} submitted "${scenario.name}" for approval — Stage 1: Finance Review`,
    });
    appendAuditEntry(scenarioId, "submitted-for-approval", {
      fromStatus: scenario.status,
      toStatus: "pending-approval",
      stageOrder: 1,
      stageName: "Finance Review",
      detail: options?.coveringNote,
    });

    return "pending-approval";
  };

  const approveScenario = async (scenarioId: string, comments?: string) => {
    if (!isDemo) {
      // Live mode: call API
      const numericId = parseInt(scenarioId, 10);
      try {
        await scenarioApi.approveScenario(numericId, {
          comments: comments ?? null,
        });
        invalidateScenarioLists();
        queryClient.invalidateQueries({ queryKey: ["pendingApprovals"] });
      } catch (error) {
        console.error("Failed to approve scenario:", error);
        toast.error("Error", {
          description: "Failed to approve scenario. Please try again.",
        });
      }
      return;
    }

    // Demo mode: stage-aware approval
    // Standard Approval: Stage 1 (Finance Review) → Stage 2 (CEO Sign-off) → Active
    // High Value Approval: Stage 1 → Stage 2 → Stage 3 → Active (same pattern)
    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario) return;

    const currentStage = scenario.currentStageOrder ?? 1;
    // Demo workflow stage counts: Standard = 2, High Value = 3
    // We treat any scenario without a specific workflow as having 2 stages
    const totalStages = 2;
    const isFinalStage = currentStage >= totalStages;

    if (isFinalStage) {
      // Final approval — go Active
      updateScenario(scenarioId, {
        status: "active",
        currentStageOrder: null,
        currentStageName: null,
        currentApprover: undefined,
        assignedApprover: undefined,
      });

      addActivity({
        type: "approval-granted",
        scenarioId,
        scenarioName: scenario.name,
        userId: currentUser.id,
        userName: getCurrentUserName(),
        message: `${getCurrentUserName()} gave final approval for "${scenario.name}" — now Active`,
      });
      appendAuditEntry(scenarioId, "approval-granted", {
        fromStatus: "pending-approval",
        toStatus: "active",
        stageOrder: currentStage,
        stageName: scenario.currentStageName ?? undefined,
        detail: comments,
      });
    } else {
      // Advance to next stage
      const nextStage = currentStage + 1;
      const nextStageName =
        nextStage === 2 ? "CEO Sign-off" : `Stage ${nextStage}`;
      // Stage 2 approver in the demo workflow is Catherine Hughes (user ID '1')
      const nextApprover = nextStage === 2 ? "1" : undefined;

      updateScenario(scenarioId, {
        status: "pending-approval",
        currentStageOrder: nextStage,
        currentStageName: nextStageName,
        currentApprover: nextApprover,
      });

      addActivity({
        type: "approval-granted",
        scenarioId,
        scenarioName: scenario.name,
        userId: currentUser.id,
        userName: getCurrentUserName(),
        message: `${getCurrentUserName()} approved "${scenario.name}" at Stage ${currentStage} — advancing to ${nextStageName}`,
      });
      appendAuditEntry(scenarioId, "approval-granted", {
        fromStatus: "pending-approval",
        toStatus: "pending-approval",
        stageOrder: currentStage,
        stageName: scenario.currentStageName ?? undefined,
        detail: comments
          ? `${comments} — advancing to ${nextStageName}`
          : `Advancing to ${nextStageName}`,
      });
    }
  };

  const rejectScenario = async (scenarioId: string, reason: string) => {
    if (!isDemo) {
      // Live mode: call API
      const numericId = parseInt(scenarioId, 10);
      try {
        await scenarioApi.rejectScenario(numericId, { reason });
        invalidateScenarioLists();
        queryClient.invalidateQueries({ queryKey: ["pendingApprovals"] });
      } catch (error) {
        console.error("Failed to reject scenario:", error);
        toast.error("Error", {
          description: "Failed to reject scenario. Please try again.",
        });
      }
      return;
    }

    // Demo mode: existing logic
    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario) return;

    updateScenario(scenarioId, {
      status: "rejected",
      currentApprover: undefined,
    });

    addActivity({
      type: "approval-rejected",
      scenarioId,
      scenarioName: scenario.name,
      userId: currentUser.id,
      userName: getCurrentUserName(),
      message: `${getCurrentUserName()} rejected "${scenario.name}": ${reason}`,
    });
    appendAuditEntry(scenarioId, "approval-rejected", {
      fromStatus: scenario.status,
      toStatus: "rejected",
      stageOrder: scenario.currentStageOrder ?? undefined,
      stageName: scenario.currentStageName ?? undefined,
      detail: reason,
    });
  };

  const withdrawApproval = async (scenarioId: string) => {
    if (!isDemo) {
      // Live mode: dedicated withdraw endpoint
      const numericId = parseInt(scenarioId, 10);
      try {
        await scenarioApi.withdrawApproval(numericId);
        invalidateScenarioLists();
        queryClient.invalidateQueries({ queryKey: ["pendingApprovals"] });
      } catch (error) {
        console.error("Failed to withdraw approval:", error);
        toast.error("Error", {
          description: "Failed to withdraw submission. Please try again.",
        });
      }
      return;
    }

    // Demo mode: reset back to draft, clear all approval and stage fields, log activity
    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario) return;

    updateScenario(scenarioId, {
      status: "draft",
      currentStageOrder: null,
      currentStageName: null,
      currentApprover: undefined,
      assignedApprover: undefined,
    });

    addActivity({
      type: "approval-withdrawn",
      scenarioId,
      scenarioName: scenario.name,
      userId: currentUser.id,
      userName: getCurrentUserName(),
      message: `${getCurrentUserName()} withdrew "${scenario.name}" from the approval workflow`,
    });
    appendAuditEntry(scenarioId, "approval-withdrawn", {
      fromStatus: scenario.status,
      toStatus: "draft",
      stageOrder: scenario.currentStageOrder ?? undefined,
      stageName: scenario.currentStageName ?? undefined,
    });
  };

  const requestChanges = async (scenarioId: string, feedback: string) => {
    if (!isDemo) {
      // Live mode: call API
      const numericId = parseInt(scenarioId, 10);
      try {
        await scenarioApi.requestChangesOnScenario(numericId, { feedback });
        invalidateScenarioLists();
        queryClient.invalidateQueries({ queryKey: ["pendingApprovals"] });
      } catch (error) {
        console.error("Failed to request changes:", error);
        toast.error("Error", {
          description: "Failed to request changes. Please try again.",
        });
      }
      return;
    }

    // Demo mode: existing logic
    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario) return;

    updateScenario(scenarioId, {
      status: "draft",
      currentApprover: undefined,
      assignedApprover: undefined,
    });

    // Add comment with feedback
    addComment(scenarioId, `Changes requested: ${feedback}`);

    addActivity({
      type: "changes-requested",
      scenarioId,
      scenarioName: scenario.name,
      userId: currentUser.id,
      userName: getCurrentUserName(),
      message: `${getCurrentUserName()} requested changes on "${scenario.name}"`,
    });
    appendAuditEntry(scenarioId, "changes-requested", {
      fromStatus: scenario.status,
      toStatus: "draft",
      stageOrder: scenario.currentStageOrder ?? undefined,
      stageName: scenario.currentStageName ?? undefined,
      detail: feedback,
    });
  };

  const markReviewed = async (scenarioId: string) => {
    if (!isDemo) {
      const numericId = parseInt(scenarioId, 10);
      try {
        await scenarioApi.markReviewed(numericId);
        invalidateScenarioLists();
        queryClient.invalidateQueries({ queryKey: ["pendingApprovals"] });
        const scenario = scenarios.find((s) => s.id === scenarioId);
        toast.success("Marked as Reviewed", {
          description: `You marked "${scenario?.name ?? "scenario"}" as reviewed`,
        });
      } catch (error) {
        console.error("Failed to mark as reviewed:", error);
        toast.error("Error", {
          description: "Failed to mark as reviewed. Please try again.",
        });
      }
      return;
    }

    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario) return;

    const reviewApproval: ScenarioApproval = {
      id: `approval-${Date.now()}`,
      userId: currentUser.id,
      approverId: currentUser.id,
      userName: getCurrentUserName(),
      userRole: "Reviewer",
      status: "reviewed" as ScenarioApproval["status"],
      timestamp: new Date(),
      order: 0,
    };

    updateScenario(scenarioId, {
      reviewedAt: new Date(),
      reviewedBy: getCurrentUserName(),
      reviewedByUserId: currentUser.id,
      approvalHistory: [...(scenario.approvalHistory ?? []), reviewApproval],
    });

    addActivity({
      type: "scenario-reviewed",
      scenarioId,
      scenarioName: scenario.name,
      userId: currentUser.id,
      userName: getCurrentUserName(),
      message: `${getCurrentUserName()} marked "${scenario.name}" as reviewed`,
    });
    appendAuditEntry(scenarioId, "reviewed", {
      fromStatus: scenario.status,
      toStatus: scenario.status,
    });

    toast.success("Marked as Reviewed", {
      description: `You marked "${scenario.name}" as reviewed`,
    });
  };

  // Parking
  const parkScenario = async (
    id: string,
    reason: string,
    reviewDate: Date,
  ): Promise<void> => {
    const scenario = scenarios.find((s) => s.id === id);
    if (!scenario) return;

    const parkedAt = new Date();
    await updateScenario(id, {
      status: "parked",
      parkedAt,
      parkedBy: getCurrentUserName(),
      parkedReason: reason,
      reviewDate,
    });

    // Live mode: patch the cached REST list so the parked status is visible on
    // the next render. enrichScenarioViews prefers the REST context status over
    // the GraphQL view status, so without this the stale 'draft' status would
    // clobber the freshly-refetched 'parked' view status.
    if (!isDemo) {
      queryClient.setQueryData<ScenarioListResponseApi>(
        ["scenarios"],
        (prev) =>
          prev
            ? {
                ...prev,
                scenarios: prev.scenarios.map((s) =>
                  String(s.id) === id
                    ? {
                        ...s,
                        status: "parked",
                        parkedAt: parkedAt.toISOString(),
                        parkedReason: reason,
                        reviewDate: reviewDate.toISOString(),
                      }
                    : s,
                ),
              }
            : prev,
      );
    }

    addActivity({
      type: "scenario-parked",
      scenarioId: id,
      scenarioName: scenario.name,
      userId: currentUser.id,
      userName: getCurrentUserName(),
      message: `${getCurrentUserName()} parked "${scenario.name}"`,
    });
    appendAuditEntry(id, "parked", {
      fromStatus: scenario.status,
      toStatus: "parked",
      detail: reason,
    });
  };

  const reactivateScenario = (id: string) => {
    const scenario = scenarios.find((s) => s.id === id);
    if (!scenario) return;

    updateScenario(id, {
      status: "draft",
      parkedAt: undefined,
      parkedBy: undefined,
      parkedReason: undefined,
      reviewDate: undefined,
    });

    addActivity({
      type: "scenario-reactivated",
      scenarioId: id,
      scenarioName: scenario.name,
      userId: currentUser.id,
      userName: getCurrentUserName(),
      message: `${getCurrentUserName()} reactivated "${scenario.name}"`,
    });
    appendAuditEntry(id, "reactivated", {
      fromStatus: scenario.status,
      toStatus: "draft",
    });
  };

  // Scenario refresh — re-pin + re-materialise against the latest base forecast.
  // Returns a promise that resolves once the live refresh has completed so
  // callers can trigger their own follow-up refetches (e.g. the detail page's
  // transactions table).
  const refreshScenario = (id: string): Promise<void> | void => {
    const scenario = scenarios.find((s) => s.id === id);
    if (!scenario) return;
    // Parked/archived scenarios are never refreshed.
    if (scenario.status === "parked" || scenario.status === "archived") return;
    // Already updating — no-op (guards against duplicate requests).
    if (scenario.freshness === "updating") return;

    if (isDemo) {
      // Simulate: flip to 'updating' immediately, settle back to 'live' after 3s.
      setDemoScenarios((prev) =>
        prev.map((s) => (s.id === id ? { ...s, freshness: "updating" } : s)),
      );
      const existing = demoRefreshTimers.current.get(id);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(() => {
        setDemoScenarios((prev) =>
          prev.map((s) => (s.id === id ? { ...s, freshness: "live" } : s)),
        );
        demoRefreshTimers.current.delete(id);
      }, 3000);
      demoRefreshTimers.current.set(id, timer);
      return;
    }

    // Live mode: enqueue the async refresh, then invalidate the views so the
    // derived freshness (Updating) is reflected on the next fetch. Also
    // invalidate the per-scenario detail queries so the chart and impact
    // figures reflect the re-materialised data on refresh.
    const numericId = parseInt(id, 10);
    return scenarioApi
      .refreshScenario(numericId)
      .then(() => {
        invalidateScenarioLists();
        queryClient.invalidateQueries({
          queryKey: ["scenario-forecast", numericId],
        });
        queryClient.invalidateQueries({
          queryKey: ["scenarioAdjustmentTotals", numericId],
        });
        queryClient.invalidateQueries({
          queryKey: ["scenarioAdjustmentsGraphql", numericId],
        });
        // Issues are reset + re-detected server-side during refresh.
        queryClient.invalidateQueries({
          queryKey: ["scenarioIssues", numericId],
        });
        queryClient.invalidateQueries({
          queryKey: ["scenarioIssuesCount", numericId],
        });
      })
      .catch((error) => {
        console.error("Failed to refresh scenario:", error);
        toast.error("Refresh failed", {
          description: "The scenario could not be refreshed. Please try again.",
        });
      });
  };

  // Collections
  const createCollection = (name: string, description?: string) => {
    const newCollection: ScenarioCollection = {
      id: `collection-${Date.now()}`,
      name,
      description,
      scenarioIds: [],
      createdBy: getCurrentUserName(),
      createdAt: new Date(),
      order: collections.length + 1,
    };
    setCollections((prev) => [...prev, newCollection]);
  };

  const addToCollection = (scenarioId: string, collectionId: string) => {
    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario) return;

    // Add to scenario's collectionIds
    updateScenario(scenarioId, {
      collectionIds: [...scenario.collectionIds, collectionId],
    });

    // Add to collection's scenarioIds
    setCollections((prev) =>
      prev.map((collection) =>
        collection.id === collectionId
          ? {
              ...collection,
              scenarioIds: [...collection.scenarioIds, scenarioId],
            }
          : collection,
      ),
    );
  };

  const removeFromCollection = (scenarioId: string, collectionId: string) => {
    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario) return;

    // Remove from scenario's collectionIds
    updateScenario(scenarioId, {
      collectionIds: scenario.collectionIds.filter((id) => id !== collectionId),
    });

    // Remove from collection's scenarioIds
    setCollections((prev) =>
      prev.map((collection) =>
        collection.id === collectionId
          ? {
              ...collection,
              scenarioIds: collection.scenarioIds.filter(
                (id) => id !== scenarioId,
              ),
            }
          : collection,
      ),
    );
  };

  // Tags
  const addTag = (scenarioId: string, tag: string) => {
    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario) return;

    if (!scenario.tags.includes(tag)) {
      updateScenario(scenarioId, {
        tags: [...scenario.tags, tag],
      });
    }
  };

  const removeTag = (scenarioId: string, tag: string) => {
    const scenario = scenarios.find((s) => s.id === scenarioId);
    if (!scenario) return;

    updateScenario(scenarioId, {
      tags: scenario.tags.filter((t) => t !== tag),
    });
  };

  // Activities
  const addActivity = (
    activity: Omit<Activity, "id" | "timestamp" | "isRead">,
  ) => {
    const newActivity: Activity = {
      ...activity,
      id: `activity-${Date.now()}`,
      timestamp: new Date(),
      isRead: false,
    };
    setActivities((prev) => [newActivity, ...prev]);
  };

  const markActivityRead = (activityId: string) => {
    setActivities((prev) =>
      prev.map((activity) =>
        activity.id === activityId ? { ...activity, isRead: true } : activity,
      ),
    );
  };

  const markAllActivitiesRead = () => {
    setActivities((prev) =>
      prev.map((activity) => ({ ...activity, isRead: true })),
    );
  };

  const dismissActivity = (activityId: string) => {
    setActivities((prev) =>
      prev.filter((activity) => activity.id !== activityId),
    );
  };

  // Reset to demo data or refetch from API
  // Versions
  const createVersion = (scenarioId: string, description: string) => {
    if (isDemo) {
      const scenario = scenarios.find((s) => s.id === scenarioId);
      if (!scenario) return;
      const newVersion: ScenarioVersion = {
        id: `v-${Date.now()}`,
        version: (scenario.currentVersion || 1) + 1,
        description,
        createdBy: currentUser.id,
        createdByName: getCurrentUserName(),
        createdAt: new Date(),
        data: {
          changes: [...scenario.changes],
          name: scenario.name,
          status: scenario.status,
          category: scenario.category,
        },
      };
      updateScenario(scenarioId, {
        versions: [...(scenario.versions || []), newVersion],
        currentVersion: newVersion.version,
      });
      return;
    }
    // Live mode
    const numericId = parseInt(scenarioId, 10);
    scenarioApi
      .createScenarioVersion(numericId, { description })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["scenarioVersions", numericId],
        });
        queryClient.invalidateQueries({
          queryKey: ["scenarioVersionsGraphql", numericId],
        });
        invalidateScenarioLists();
      })
      .catch((error) => {
        console.error("Failed to create version:", error);
      });
  };

  const resetToDemo = () => {
    if (isDemo) {
      setDemoScenarios(COMPREHENSIVE_SCENARIOS);
      setCollections(SCENARIO_COLLECTIONS);
      setActivities(ACTIVITIES);
    } else {
      invalidateScenarioLists();
      queryClient.invalidateQueries({ queryKey: ["scenarioChanges"] });
    }
  };

  return (
    <ScenarioContext.Provider
      value={{
        scenarios,
        collections,
        activities,
        pendingApprovalCount,
        pendingApprovalScenarios,
        createScenario,
        updateScenario,
        deleteScenario,
        cloneScenario,
        toggleFavorite,
        setPriority,
        setStatus,
        setLabel,
        addChange,
        updateChange,
        removeChange,
        addComment,
        replyToComment,
        reactToComment,
        shareScenario,
        updateShare,
        removeShare,
        requestApproval,
        approveScenario,
        rejectScenario,
        withdrawApproval,
        requestChanges,
        markReviewed,
        parkScenario,
        reactivateScenario,
        refreshScenario,
        createCollection,
        addToCollection,
        removeFromCollection,
        addTag,
        removeTag,
        addActivity,
        markActivityRead,
        markAllActivitiesRead,
        dismissActivity,
        createVersion,
        resetToDemo,
        currentUser: currentUser.id,
        setCurrentUser,
      }}
    >
      {children}
    </ScenarioContext.Provider>
  );
};
